import api from "./api"
import type { FieldDefinition, FieldOption, FieldType, SectionNode } from "./content-types"
import type { Checklist } from "./checklists"

export interface AuditSetScopeRef {
  id: string
  title: string
  code: string | null
  parentId: string | null
}

/**
 * Denetim setinin revizyon/onay durumu — kontrol listesindekiyle AYNI makine.
 *
 * `ARCHIVED` yalnız eski kayıtlarda görülür; yeni akış onun yerine `CANCELLED`
 * üretiyor. Ekranda seçilemez, salt gösterilir.
 */
export type AuditSetStatus = "DRAFT" | "IN_REVIEW" | "PUBLISHED" | "CANCELLED" | "ARCHIVED"

export const AUDIT_SET_STATUS_LABEL: Record<AuditSetStatus, string> = {
  DRAFT: "Taslak",
  IN_REVIEW: "Onayda",
  PUBLISHED: "Yayında",
  CANCELLED: "İptal",
  ARCHIVED: "Arşiv",
}

/** Rozet renkleri — liste ve detay aynı yerden okusun diye burada. */
export const AUDIT_SET_STATUS_BADGE: Record<AuditSetStatus, string> = {
  DRAFT: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  IN_REVIEW: "bg-blue-100 text-blue-800 hover:bg-blue-100",
  PUBLISHED: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  CANCELLED: "bg-rose-100 text-rose-800 hover:bg-rose-100",
  ARCHIVED: "bg-muted text-muted-foreground hover:bg-muted",
}

export interface AuditSet {
  id: string
  name: string
  description: string | null
  isActive: boolean
  scopeId: string | null
  scope?: AuditSetScopeRef | null
  companyId?: string | null
  isTemplate?: boolean
  /** Künye */
  code: string | null
  revisionNo: string | null
  status: AuditSetStatus
  publishDate: string | null
  /** Yürürlük tarihi — ilk yayında atanır, sonraki revizyonlara taşınır. */
  effectiveDate: string | null
  revisionDate: string | null
  createdByUserId: string | null

  /** Revizyon zinciri */
  revisionFamilyId: string
  revisionSeq: number
  isCurrentRevision: boolean
  revisionStartedAt: string | null
  revisionStartedByUserId: string | null
  revisionNote: string | null
  publishedAt: string | null
  publishedByUserId: string | null

  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface AuditSetWithCounts extends AuditSet {
  _count: {
    checklists: number
  }
}

export interface AuditSetWithForms extends AuditSet {
  checklists: Checklist[]
}

export interface PaginatedAuditSets {
  data: AuditSetWithCounts[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface AuditSetParams {
  page?: number
  limit?: number
  search?: string
  status?: string
}

export interface CreateAuditSetPayload {
  name: string
  description?: string
  scopeId?: string | null
  code?: string | null
  revisionNo?: string | null
  status?: AuditSetStatus
}

export interface UpdateAuditSetPayload {
  name?: string
  description?: string
  scopeId?: string | null
  code?: string | null
  revisionNo?: string | null
  /** Sunucu bunu YOK SAYAR — durum onay sürecinin çıktısı. Tip uyumu için var. */
  status?: AuditSetStatus
  publishDate?: string | null
  effectiveDate?: string | null
  revisionDate?: string | null
  /** ARŞİV: false = pasif; listelerde gizlenir, silinmez. */
  isActive?: boolean
}

export async function fetchAuditSets(params: AuditSetParams = {}) {
  const { data } = await api.get<PaginatedAuditSets>("/audit-sets", { params })
  return data
}

export async function fetchAuditSet(id: string) {
  const { data } = await api.get<AuditSetWithForms>(`/audit-sets/${id}`)
  return data
}

export async function createAuditSet(payload: CreateAuditSetPayload) {
  const { data } = await api.post<AuditSet>("/audit-sets", payload)
  return data
}

export async function updateAuditSet(id: string, payload: UpdateAuditSetPayload) {
  const { data } = await api.patch<AuditSet>(`/audit-sets/${id}`, payload)
  return data
}

export async function deleteAuditSet(id: string) {
  await api.delete(`/audit-sets/${id}`)
}

// === Audex (sistem) setleri ===

// Admin + company: tüm Audex setleri.
export async function fetchSystemAuditSets() {
  const { data } = await api.get<AuditSetWithCounts[]>("/audit-sets/system/list")
  return data
}

// Admin: bir denetim setini Audex seti olarak tanımla (kopya üretir).
export async function templatizeAuditSet(id: string) {
  const { data } = await api.post<AuditSet>(`/audit-sets/${id}/templatize`)
  return data
}

// Admin/company: Audex setini kullan (kendi düzenlenebilir kopyasını alır).
export async function useSystemAuditSet(id: string) {
  const { data } = await api.post<AuditSet>(`/audit-sets/${id}/use`)
  return data
}

// === Setin içeriği: menü ağacı + yerleştirilen öğeler ===

/** Bir öğenin hangi kaynağa baktığı. */
export type AuditSetItemKind = "CHECKLIST" | "CONTENT_TYPE" | "DOCUMENT"

export const AUDIT_SET_ITEM_KIND_LABEL: Record<AuditSetItemKind, string> = {
  CHECKLIST: "Kontrol Listesi",
  CONTENT_TYPE: "Form",
  DOCUMENT: "Doküman",
}

export interface AuditSetMenu {
  id: string
  auditSetId: string
  parentId: string | null
  label: string
  sortOrder: number
}

export interface AuditSetItem {
  id: string
  auditSetId: string
  menuId: string | null
  kind: AuditSetItemKind
  sortOrder: number
  /** Elle verilen etiket; boşsa `title` kaynağın adını taşır. */
  label: string | null
  /** Bağlanan kaynağın id'si (kontrol listesi / içerik tipi / doküman). */
  refId: string | null
  /** Gösterilecek ad — `label` varsa o, yoksa kaynağınki. */
  title: string
  code: string | null
  revisionNo: string | null
  /** İçerik tipi öğelerinde doldurma ekranına gitmek için. */
  contentTypeKey: string | null
  /** Doküman öğelerinde önizleme/indirme için. */
  fileId: string | null
  /** Form öğelerinde görevlendirme: formda görevi olan birimler (görev türü kodları). */
  taskTypeCodes?: string[]
  /** ESKİ tek birim — `taskTypeCodes[0]`. */
  taskTypeCode?: string | null
}

/** Formun birimleri — yeni liste, yoksa eski tek alan. */
export const formBirimleri = (item: Pick<AuditSetItem, "taskTypeCodes" | "taskTypeCode">) =>
  item.taskTypeCodes?.length ? item.taskTypeCodes : item.taskTypeCode ? [item.taskTypeCode] : []

export interface AuditSetContent {
  menus: AuditSetMenu[]
  items: AuditSetItem[]
}

export async function fetchAuditSetContent(auditSetId: string) {
  const { data } = await api.get<AuditSetContent>(`/audit-sets/${auditSetId}/content`)
  return data
}

// === Set alanları (formların alanları, anahtara göre tekil) ===============

/** Setteki formlarda geçen bir alan — aynı anahtar tek alan. */
export interface AuditSetField {
  key: string
  label: string
  fieldType: FieldType
  required: boolean
  options: FieldOption[] | string[] | null
  helpText: string | null
  placeholder: string | null
  labelPosition: string | null
  optionLayout?: string | null
  optionsFromKey: string | null
  decimals: number | null
  formula: string | null
  /** Bu alanı kullanan formlar. */
  forms: { id: string; key: string; label: string; code: string | null }[]
  /** Alanın formdaki bölümü (ilk göründüğü) — elle gruplanmayanlar buna göre gruplanır. */
  section?: { key: string; label: string } | null
  /**
   * Formda mı doldurulur? `true`: Genel Bilgiler'de sorulmaz, dosyadaki her
   * form örneğinde ayrı doldurulur. Değerlendirme tabloları her zaman formda.
   */
  formda?: boolean
}

/** Set alanlarının bir grubu — Genel Bilgiler'de bir bölüm. */
export interface AuditSetFieldGroup {
  id: string
  label: string
  keys: string[]
}

export interface AuditSetFields {
  fields: AuditSetField[]
  groups: AuditSetFieldGroup[]
  /** Hiçbir gruba konmamış anahtarlar (Genel Bilgiler'de "Diğer Alanlar"). */
  ungrouped: string[]
  /** Formda doldurulan alanların anahtarları (Genel Bilgiler'den GELMEZ). */
  formda?: string[]
}

export async function fetchAuditSetFields(auditSetId: string) {
  const { data } = await api.get<AuditSetFields>(`/audit-sets/${auditSetId}/fields`)
  return data
}

/** Formda doldurulacak alanları kaydeder (kalanlar Genel Bilgiler'den gelir). */
export async function saveAuditSetFormFields(auditSetId: string, keys: string[]) {
  const { data } = await api.put<AuditSetFields>(`/audit-sets/${auditSetId}/form-fields`, { keys })
  return data
}

export async function saveAuditSetFieldGroups(auditSetId: string, groups: AuditSetFieldGroup[]) {
  const { data } = await api.put<AuditSetFields>(`/audit-sets/${auditSetId}/field-groups`, { groups })
  return data
}

/**
 * Set alanlarını form bölümlerine çevirir: setin Alanlar sekmesindeki her grup
 * bir bölüm; oraya konmamış alanlar FORMDAKİ BÖLÜMLERİNE göre gruplanır
 * ("Asansör Bilgileri", "Başvuru Sahibi Bilgileri"…) — kapsamın kendi
 * alanlarının kitaplık gruplarıyla gelmesi gibi. Aynı adlı bölümler (farklı
 * formlarda) tek grupta birleşir; bölümü bilinmeyenler "Diğer Alanlar".
 * `atla`daki anahtarlar (ör. kapsamın varsayılan grubunda zaten sorulanlar)
 * çıkarılır — alan tek kaynaklı, iki yerde sorulmaz.
 */
export function setFieldsToSections(set: AuditSetFields, atla: Set<string> = new Set()): SectionNode[] {
  // Formda doldurulan alanlar Genel Bilgiler'de SORULMAZ.
  const byKey = new Map(set.fields.filter((f) => !f.formda).map((f) => [f.key, f]))
  const alan = (f: AuditSetField, i: number): FieldDefinition => ({
    id: `set-${f.key}`,
    key: f.key,
    label: f.label,
    fieldType: f.fieldType,
    required: f.required,
    options: f.options,
    validation: null,
    defaultValue: null,
    visibleWhen: null,
    helpText: f.helpText,
    placeholder: f.placeholder,
    labelPosition: f.labelPosition,
    optionLayout: f.optionLayout,
    optionsFromKey: f.optionsFromKey,
    decimals: f.decimals,
    formula: f.formula,
    sortOrder: i,
  })
  const bolum = (id: string, label: string, keys: string[], sortOrder: number): SectionNode | null => {
    const fields = keys
      .filter((k) => !atla.has(k))
      .map((k) => byKey.get(k))
      .filter((f): f is AuditSetField => !!f)
      .map(alan)
    if (fields.length === 0) return null
    return { id: `set-grup-${id}`, key: `set_grup_${id}`, label, kind: "SECTION", repeatable: false, sortOrder, fields, children: [] }
  }

  // Gruplanmamışlar: form bölümüne göre, ilk görünüş sırasıyla.
  const kovalar = new Map<string, { id: string; label: string; keys: string[] }>()
  const diger: string[] = []
  for (const k of set.ungrouped) {
    const b = byKey.get(k)?.section
    const ad = b?.label?.trim()
    if (!b || !ad) {
      diger.push(k)
      continue
    }
    const anahtar = ad.toLocaleLowerCase("tr-TR")
    const kova = kovalar.get(anahtar) ?? { id: `b-${kovalar.size}-${b.key}`, label: ad, keys: [] }
    kova.keys.push(k)
    kovalar.set(anahtar, kova)
  }
  const bolumGruplari = [...kovalar.values()]
  const sayi = set.groups.length + bolumGruplari.length

  return [
    ...set.groups.map((g, i) => bolum(g.id, g.label, g.keys, i)),
    ...bolumGruplari.map((g, i) => bolum(g.id, g.label, g.keys, set.groups.length + i)),
    bolum("diger", sayi > 0 ? "Diğer Alanlar" : "Denetim Seti Alanları", diger, sayi),
  ].filter((s): s is SectionNode => !!s)
}

export async function createAuditSetMenu(
  auditSetId: string,
  payload: { label: string; parentId?: string | null },
) {
  const { data } = await api.post<AuditSetMenu>(`/audit-sets/${auditSetId}/menus`, payload)
  return data
}

export async function updateAuditSetMenu(
  auditSetId: string,
  menuId: string,
  payload: { label?: string; parentId?: string | null; sortOrder?: number },
) {
  const { data } = await api.patch<AuditSetMenu>(
    `/audit-sets/${auditSetId}/menus/${menuId}`,
    payload,
  )
  return data
}

/** Menüyü siler; alt menüleri ve öğeleri bir üst seviyeye devreder (backend). */
export async function deleteAuditSetMenu(auditSetId: string, menuId: string) {
  await api.delete(`/audit-sets/${auditSetId}/menus/${menuId}`)
}

export async function reorderAuditSetMenus(auditSetId: string, ids: string[]) {
  await api.patch(`/audit-sets/${auditSetId}/menus/reorder`, { ids })
}

/** Toplu bağlama — seçici diyalogları çoklu seçiyor. */
export async function addAuditSetItems(
  auditSetId: string,
  payload: { kind: AuditSetItemKind; refIds: string[]; menuId?: string | null },
) {
  const { data } = await api.post<AuditSetItem[]>(`/audit-sets/${auditSetId}/items`, payload)
  return data
}

export async function updateAuditSetItem(
  auditSetId: string,
  itemId: string,
  payload: { menuId?: string | null; sortOrder?: number; label?: string | null; taskTypeCode?: string | null },
) {
  const { data } = await api.patch<AuditSetItem>(
    `/audit-sets/${auditSetId}/items/${itemId}`,
    payload,
  )
  return data
}

/**
 * Formun görevlendirmesi: görevi olan birimler (boş = kaldır). Set içeriği
 * değil: yayındaki sette de değişir.
 */
export async function setAuditSetItemTaskTypes(auditSetId: string, itemId: string, taskTypeCodes: string[]) {
  const { data } = await api.patch<AuditSetItem>(`/audit-sets/${auditSetId}/items/${itemId}/task-type`, { taskTypeCodes })
  return data
}

export async function removeAuditSetItem(auditSetId: string, itemId: string) {
  await api.delete(`/audit-sets/${auditSetId}/items/${itemId}`)
}

export async function reorderAuditSetItems(auditSetId: string, ids: string[]) {
  await api.patch(`/audit-sets/${auditSetId}/items/reorder`, { ids })
}


// ==========================================================
// Revizyon zinciri + onay süreci
//
// Kontrol listesindeki API ile BİREBİR aynı şekil; iki ekran aynı dili
// konuşsun diye alan adları da aynı tutuldu.
// ==========================================================

export type AuditSetApprovalDecision = "PENDING" | "APPROVED" | "REJECTED"

export type AuditSetRevisionEventType =
  | "CREATED" | "SUBMITTED" | "REMINDED" | "APPROVED" | "REJECTED"
  | "PUBLISHED" | "CANCELLED" | "APPROVER_REMOVED" | "REVOKED"
  | "PREPARER_CHANGED"

/** Onay/olay satırlarında gösterilen kullanıcı özeti. */
export interface AuditSetApprovalUser {
  id: string
  email: string
  personnel: { firstName: string; lastName: string } | null
}

export interface AuditSetApproval {
  id: string
  userId: string
  decision: AuditSetApprovalDecision
  note: string | null
  decidedAt: string | null
  notifiedAt: string | null
  sortOrder: number
  user: AuditSetApprovalUser
}

export interface AuditSetRevisionEvent {
  id: string
  type: AuditSetRevisionEventType
  note: string | null
  createdAt: string
  user: AuditSetApprovalUser | null
}

export interface AuditSetRevisionState {
  id: string
  status: AuditSetStatus
  isCurrentRevision: boolean
  publishedAt: string | null
  publishedByUserId: string | null
  revisionStartedByUserId: string | null
  createdByUserId: string | null
  /** HAZIRLAYAN — seti hazır sayan tek kişi; onaya yalnız o gönderebilir. */
  preparedByUserId: string | null
  preparedByName: string | null
  approvals: AuditSetApproval[]
  events: AuditSetRevisionEvent[]
}

/** Revizyon listesi satırı (sol sütunun altındaki liste). */
export interface AuditSetRevision {
  id: string
  name: string
  code: string | null
  description: string | null
  revisionNo: string | null
  revisionDate: string | null
  revisionSeq: number
  isCurrentRevision: boolean
  status: AuditSetStatus
  publishedAt: string | null
  publishDate: string | null
  effectiveDate: string | null
  revisionStartedAt: string | null
  revisionStartedByUserId: string | null
  revisionNote: string | null
  createdAt: string
  updatedAt: string
  _count: { menus: number; items: number }
}

export interface AuditSetApproverCandidate extends AuditSetApprovalUser {
  role: { name: string } | null
}

/** Kullanıcının adı yoksa e-postaya düş — satır boş görünmesin. */
export function auditSetApprovalUserLabel(
  user: AuditSetApprovalUser | null | undefined,
): string {
  if (!user) return "—"
  const name = [user.personnel?.firstName, user.personnel?.lastName]
    .filter(Boolean).join(" ").trim()
  return name || user.email
}

/**
 * Ekranda gösterilecek revizyon numarası.
 *
 * Kalite dokümanlarında ilk yayın "00"; `revisionSeq` 1'den başladığı için bir
 * eksiltip iki haneye tamamlıyoruz. `revisionNo` boşsa (eski kayıt) sıradan
 * türetiliyor — veriyi geriye dönük değiştirmeden.
 */
export function displayAuditSetRevisionNo(
  r: { revisionNo?: string | null; revisionSeq?: number },
): string {
  return r.revisionNo ?? String(Math.max(0, (r.revisionSeq ?? 1) - 1)).padStart(2, "0")
}

export async function fetchAuditSetRevisions(id: string) {
  const { data } = await api.get<AuditSetRevision[]>(`/audit-sets/${id}/revisions`)
  return data
}

export async function createAuditSetRevision(
  id: string,
  payload: { revisionNo?: string; revisionDate?: string; note?: string } = {},
) {
  const { data } = await api.post<AuditSet>(`/audit-sets/${id}/revisions`, payload)
  return data
}

export async function deleteAuditSetRevision(id: string, revisionId: string) {
  await api.delete(`/audit-sets/${id}/revisions/${revisionId}`)
}

export async function fetchAuditSetApprovalState(id: string) {
  const { data } = await api.get<AuditSetRevisionState>(`/audit-sets/${id}/approval`)
  return data
}

export async function fetchAuditSetApproverCandidates(id: string) {
  const { data } = await api.get<AuditSetApproverCandidate[]>(
    `/audit-sets/${id}/approver-candidates`,
  )
  return data
}

/** HAZIRLAYAN devri — onay sürecinde reddedilir (409). */
export async function setAuditSetPreparer(id: string, userId: string) {
  const { data } = await api.put<{ preparedByUserId: string; preparedByName: string | null }>(
    `/audit-sets/${id}/preparer`,
    { userId },
  )
  return data
}

export async function setAuditSetApprovers(id: string, userIds: string[]) {
  const { data } = await api.put<AuditSetApproval[]>(`/audit-sets/${id}/approvers`, { userIds })
  return data
}

/** Tek onaycıyı çıkarır — onay SÜRECİNDE de çalışır (liste baştan yazılmaz). */
export async function removeAuditSetApprover(id: string, userId: string) {
  const { data } = await api.delete<AuditSetApproval[]>(`/audit-sets/${id}/approvers/${userId}`)
  return data
}

export async function submitAuditSetForApproval(id: string, note?: string) {
  const { data } = await api.post<AuditSetRevisionState>(`/audit-sets/${id}/approval/submit`, { note })
  return data
}

export async function withdrawAuditSetApproval(id: string, note?: string) {
  const { data } = await api.post<AuditSetRevisionState>(`/audit-sets/${id}/approval/withdraw`, { note })
  return data
}

export async function remindAuditSetApprovers(id: string) {
  const { data } = await api.post<AuditSetRevisionState>(`/audit-sets/${id}/approval/remind`, {})
  return data
}

export async function decideAuditSetApproval(
  id: string,
  decision: "APPROVED" | "REJECTED",
  note?: string,
) {
  const { data } = await api.post<AuditSetRevisionState>(
    `/audit-sets/${id}/approval/decision`, { decision, note },
  )
  return data
}

export async function publishAuditSetRevision(id: string, note?: string) {
  const { data } = await api.post<AuditSetRevisionState>(`/audit-sets/${id}/approval/publish`, { note })
  return data
}

export async function revokeAuditSetRevision(id: string, note?: string) {
  const { data } = await api.post<AuditSetRevisionState>(`/audit-sets/${id}/approval/revoke`, { note })
  return data
}

// === Set notları ===

export interface AuditSetNote {
  id: string
  body: string
  sortOrder: number
  createdByUserId: string | null
  createdAt: string
  updatedAt: string
}

export async function fetchAuditSetNotes(id: string) {
  const { data } = await api.get<AuditSetNote[]>(`/audit-sets/${id}/notes`)
  return data
}

export async function createAuditSetNote(id: string, body: string) {
  const { data } = await api.post<AuditSetNote>(`/audit-sets/${id}/notes`, { body })
  return data
}

export async function updateAuditSetNote(id: string, noteId: string, body: string) {
  const { data } = await api.patch<AuditSetNote>(`/audit-sets/${id}/notes/${noteId}`, { body })
  return data
}

export async function deleteAuditSetNote(id: string, noteId: string) {
  await api.delete(`/audit-sets/${id}/notes/${noteId}`)
}

export async function reorderAuditSetNotes(id: string, orderedIds: string[]) {
  await api.patch(`/audit-sets/${id}/notes/reorder`, { orderedIds })
}

// === Şablon paylaşımı ===

export async function fetchAuditSetShares(id: string) {
  const { data } = await api.get<string[]>(`/audit-sets/${id}/shares`)
  return data
}

export async function setAuditSetShares(id: string, companyIds: string[]) {
  const { data } = await api.put<string[]>(`/audit-sets/${id}/shares`, { companyIds })
  return data
}
